import type {HideableValue, RichTextContent, Values} from '@wix/devzai-utils-common';
import {
    hideableValueCreateHidden,
} from '@wix/devzai-utils-common';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import type {
    BymoPagePropsContentSettingsMetadata,
} from '../../client-server-common';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    BymoPagePropsSpecialContentSettingMetadata,
} from '../../client-server-common';
import type {DoppeActionPrice} from '../../client-server-common/types/doppe-action-price';
import type {DoppeActionType} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {
    doppeActionTypeDefineMetadata,
} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsResetSettingsForLock,
} from '../../client-server-common/types/doppe-action-cta-button';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';

/**
 * `doppeActionTypeDownloadFilePreview` — a content widget for a downloadable
 * file, modeled on `doppeActionTypeProduct`. Renders an image, title,
 * RichText description, and price; supports content sections and CTA buttons.
 *
 * Distinct from `doppeActionTypeDownloadFile` (which is button-only) and from
 * `DoppeActionCTAType.DownloadFile` (a CTA option nested inside other actions):
 * the preview widget seeds a non-deletable `DoppeActionCTAType.DownloadMainFile`
 * CTA into `ctaButtons[0]` at action creation. That CTA carries no own settings
 * — its label uses the standard `MultipleButton.text`, and at click time it
 * sources `fileId` / `price` / `title` / `description` directly from the
 * parent action's `actionSettings`.
 *
 * Click flow (free): server mints a signed URL → browser navigates.
 * Click flow (paid): Buy modal → Wix Checkout → native thank-you modal
 * (post-purchase Download button). Both paths reuse the same primitives as the
 * other Download File entry points via `doppeViewerDownloadFileResolveOnClick`.
 */
export const DoppeActionTypeDownloadFilePreviewAdditionalSectionsDisplay = {
    None: 'None',
    Start: 'Start',
    End: 'End',
} as const;

export type DoppeActionTypeDownloadFilePreviewAdditionalSectionsDisplay = Values<typeof DoppeActionTypeDownloadFilePreviewAdditionalSectionsDisplay>;

export namespace DoppeActionTypeDownloadFilePreview {

    /**
     * The bound private-media file. Always all-or-nothing: when a file is
     * selected, both `fileId` (the Wix Media Manager storage key) and
     * `fileName` (the original picker title — used as the read-only label in
     * the settings panel, and as the file's display name in the live-preview
     * download toast) are present. When no file is selected, the whole
     * struct is `null` — the type makes it impossible to persist a
     * `fileName` without its corresponding `fileId`.
     */
    export interface FileSettings {
        fileId: string;
        fileName: string;
    }

    /**
     * Wix-Media-Manager-bound file plus optional payment gate.
     *
     * `price` is the single source of truth for both checkout and display:
     * hidden = free download (server mints a signed URL on click); visible =
     * paid download at the configured price (Buy modal → Wix Checkout →
     * thank-you modal). The `DoppeActionCTAType.DownloadMainFile` CTA wraps
     * this into the `DownloadFile` click-options shape at click time —
     * `productName` is derived from `title`, `customItemTypeDescription`
     * from `description`'s plain text, and `quantity` is always `1`.
     */
    export interface DownloadSettings {
        /** `null` = no file selected yet. Otherwise carries `{fileId, fileName}` together. */
        fileSettings: FileSettings | null;
        /** Hidden = free download. Visible = paid download at the configured price. */
        price: HideableValue<DoppeActionPrice>;
    }

    /**
     * User-visible preview content. `title` is reused as the Wix Checkout
     * line item's `productName`; the `description` is converted to plain text
     * and passed as the Wix Checkout line item's `itemTypeDescription`.
     *
     * The displayed price string is derived at viewer time from `price` +
     * the viewer's language (via `doppeActionPriceGetFormattedPrice`).
     *
     * The download button's label is NOT stored here — it lives on the
     * seeded `DoppeActionCTAType.DownloadMainFile` CTA's `MultipleButton.text`,
     * just like every other CTA's label.
     */
    export interface ContentSettings {
        /**
         * Preview images. Today the widget renders only `media[0]` — the
         * array shape is forward-compat for a future gallery.
         */
        media: WixMediaResource[];
        /** Always rendered (non-hideable). Acts as the action's headline. */
        title: string;
        description: HideableValue<RichTextContent>;
    }

    export interface ActionSettings extends
        ContentSettings,
        DownloadSettings,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {
        displayAdditionalSections: DoppeActionTypeDownloadFilePreviewAdditionalSectionsDisplay;
    }

    export type PartialProps = Partial<ActionSettings>;
}

export const doppeActionTypeDownloadFilePreviewActionContentSettingsMetadata = {
    title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
    description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
    media: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
    fileSettings: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    price: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
} satisfies BymoPagePropsContentSettingsMetadata<DoppeActionTypeDownloadFilePreview.ActionSettings>;

export const doppeActionTypeDownloadFilePreviewSettingsMetadata = {
    contentSettingsMetadata: doppeActionTypeDownloadFilePreviewActionContentSettingsMetadata,
    supportsActionCtaButtons: {
        customCtaButtonTypes: {}
    },
    supportsContentSections: {
        customContentSectionsTypes: {}
    },
    hideableContentSettingsNames: {
        description: true,
        price: true,
    },
    bindableContentSettingsNames: {},
} satisfies DoppeActionType.SettingsMetadata<DoppeActionTypeDownloadFilePreview.ActionSettings>;

export const doppeActionTypeDownloadFilePreview = doppeActionTypeDefineMetadata<DoppeActionTypeDownloadFilePreview.ActionSettings, DoppeActionTypeDownloadFilePreview.PartialProps>({
    id: 'io.bymo.action/download-file-preview',
    resolveActionSettings: action => {

        const {
            fileSettings = null,
            price = hideableValueCreateHidden({actualPrice: 5, currencyCode: 'USD'}),
            media = [],
            title = '',
            description = hideableValueCreateHidden(''),
            sections,
            ctaButtons,
            displayAdditionalSections = DoppeActionTypeDownloadFilePreviewAdditionalSectionsDisplay.None,
            ...restSettings
        } = action.settings;

        return {
            fileSettings: fileSettings,
            price: price,
            media: media,
            title: title,
            description: description,
            displayAdditionalSections: displayAdditionalSections,
            ...doppeActionContentSectionSettingsDefaultValuesMigration({sections}),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration({ctaButtons: ctaButtons}, false),
            ...restSettings,
        };
    },
    actionSettingsMetadata: doppeActionTypeDownloadFilePreviewSettingsMetadata,
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    isLinkOpener: false,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, settings => {
            doppeActionCtaButtonsResetSettingsForLock(settings);
        }, options);
    },
});
